<template>
  <el-drawer
    :visible.sync="innerVisible"
    direction="rtl"
    size="88%"
    append-to-body
    :wrapper-closable="true"
    custom-class="device-alarm-rules-drawer"
    :with-header="false"
    @open="refreshSections"
    @closed="onClosed"
  >
    <div class="alarm-drawer-wrap">
      <div class="drawer-head">
        <div>
          <div class="title">报警设置</div>
          <div class="sub">{{ deviceTitle }}</div>
        </div>
        <div class="head-actions">
          <el-button type="primary" size="small" icon="el-icon-plus" @click="openCreate()">新增报警设置</el-button>
          <el-button type="text" icon="el-icon-close" class="close-btn" @click="innerVisible = false" />
        </div>
      </div>

      <el-alert
        class="hint"
        type="info"
        :closable="false"
        show-icon
        title="这里只显示绑定到当前设备的报警规则，各设备独立设置、互不影响；未绑定设备的全局规则请在“告警管理”中维护。"
      />

      <div class="metric-sections">
        <div v-for="section in sections" :key="section.key" class="metric-section">
          <div class="metric-head">
            <div>
              <span class="metric-name">{{ section.label }}</span>
              <span v-if="section.unit" class="metric-unit">{{ section.unit }}</span>
            </div>
            <div class="metric-actions">
              <span class="metric-current">当前值：{{ section.currentText }}</span>
              <el-button type="text" size="small" icon="el-icon-plus" @click="openCreate(section)">新增</el-button>
            </div>
          </div>

          <el-table v-if="section.rules.length" :data="section.rules" border size="small">
            <el-table-column label="报警名称" min-width="150" show-overflow-tooltip>
              <template slot-scope="{ row }">
                <div class="rule-name">{{ row.ruleName || '未命名规则' }}</div>
                <div class="rule-desc">{{ row.ruleDesc || row.paramCondition || '—' }}</div>
              </template>
            </el-table-column>
            <el-table-column label="类别" width="90" align="center">
              <template slot-scope="{ row }">{{ categoryLabel(row.alarmCategory) }}</template>
            </el-table-column>
            <el-table-column label="级别" width="82" align="center">
              <template slot-scope="{ row }">{{ levelLabel(row.alarmLevel) }}</template>
            </el-table-column>
            <el-table-column label="下限" width="150">
              <template slot-scope="{ row }">
                <el-input v-model="row.thresholdLower" size="mini" clearable placeholder="低于报警" />
              </template>
            </el-table-column>
            <el-table-column label="上限" width="150">
              <template slot-scope="{ row }">
                <el-input v-model="row.thresholdUpper" size="mini" clearable placeholder="超过报警" />
              </template>
            </el-table-column>
            <el-table-column label="启用" width="92" align="center">
              <template slot-scope="{ row }">
                <el-switch v-model="row.enabled" active-value="0" inactive-value="1" />
              </template>
            </el-table-column>
            <el-table-column label="操作" width="110" align="center" fixed="right">
              <template slot-scope="{ row }">
                <el-button type="text" size="small" :loading="savingRuleId === row.ruleId" @click="saveRule(row, section)">
                  保存
                </el-button>
              </template>
            </el-table-column>
          </el-table>

          <div v-else class="empty-rule">
            暂无匹配的报警规则
            <el-button type="text" size="small" @click="openCreate(section)">新增报警设置</el-button>
          </div>
        </div>
      </div>
    </div>

    <el-dialog
      title="新增报警设置"
      :visible.sync="createVisible"
      width="680px"
      append-to-body
      custom-class="device-alarm-create-dialog"
      @closed="resetCreateForm"
    >
      <el-form :model="createForm" label-width="108px" size="small">
        <el-form-item label="绑定设备">
          <el-input :value="deviceTitle" disabled />
        </el-form-item>
        <el-form-item label="监测指标" required>
          <el-select v-model="createForm.metricKey" style="width:100%" @change="onCreateMetricChange">
            <el-option v-for="m in metrics" :key="m.key" :label="`${m.label}${m.unit ? '（' + m.unit + '）' : ''}`" :value="m.key" />
          </el-select>
        </el-form-item>
        <el-form-item label="报警名称" required>
          <el-input v-model="createForm.ruleName" placeholder="如 压力上限报警" />
        </el-form-item>
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="阈值下限">
              <el-input v-model="createForm.thresholdLower" clearable placeholder="低于该值报警" @input="syncCreateParamCondition" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="阈值上限">
              <el-input v-model="createForm.thresholdUpper" clearable placeholder="超过该值报警" @input="syncCreateParamCondition" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="报警级别">
              <el-select v-model="createForm.alarmLevel" style="width:100%">
                <el-option label="紧急" value="critical" />
                <el-option label="重要" value="warning" />
                <el-option label="一般" value="info" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="启用状态">
              <el-switch v-model="createForm.enabled" active-value="0" inactive-value="1" active-text="启用" inactive-text="停用" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="执行条件">
          <el-input v-model="createForm.paramCondition" type="textarea" :rows="2" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="createVisible = false">取消</el-button>
        <el-button type="primary" :loading="createSaving" @click="createRule">保存</el-button>
      </span>
    </el-dialog>
  </el-drawer>
</template>

<script>
import { gasApi } from '@/api/gas'
import { METRIC_ALARM_CATEGORY, findMatchingAlarmRules } from '@/utils/monitorAlarmThreshold'

const CATEGORY_MAP = {
  flow: '流量类',
  pressure: '压力类',
  water_quality: '泄漏类',
  device_status: '设备状态',
  temperature: '温度类'
}

const LEVEL_MAP = {
  critical: '紧急',
  warning: '重要',
  info: '一般'
}

export default {
  name: 'DeviceAlarmRulesDrawer',
  props: {
    visible: { type: Boolean, default: false },
    rules: { type: Array, default: () => [] },
    device: { type: Object, default: null },
    metrics: { type: Array, default: () => [] }
  },
  data() {
    return {
      sections: [],
      savingRuleId: null,
      createVisible: false,
      createSaving: false,
      createForm: {}
    }
  },
  computed: {
    innerVisible: {
      get() { return this.visible },
      set(v) { this.$emit('update:visible', v) }
    },
    deviceTitle() {
      const d = this.device || {}
      const name = d.terminalName || d.deviceName || '未命名设备'
      const code = d.deviceCode || d.terminalCode || '—'
      const type = d.deviceType || d.terminalType || '—'
      return `${name} · ${code} · ${type}`
    }
  },
  watch: {
    visible(val) {
      if (val) this.refreshSections()
    },
    rules: {
      deep: true,
      handler() {
        if (this.visible) this.refreshSections()
      }
    },
    metrics: {
      deep: true,
      handler() {
        if (this.visible) this.refreshSections()
      }
    }
  },
  methods: {
    refreshSections() {
      const d = this.device || {}
      const deviceType = d.deviceType || d.terminalType || ''
      const deviceCode = d.deviceCode || d.terminalCode || ''
      const siteName = d.siteName || ''
      const deviceId = d.deviceId || null
      this.sections = (this.metrics || []).map(metric => {
        const matched = findMatchingAlarmRules(this.rules, deviceType, metric.key, {
          deviceId,
          deviceCode,
          siteName,
          boundOnly: true
        })
        const value = this.metricValue(d, metric)
        return {
          ...metric,
          currentText: this.formatValue(value),
          rules: matched.map(rule => this.toDraft(rule))
        }
      })
    },
    toDraft(rule) {
      return {
        ...rule,
        thresholdLower: rule.thresholdLower == null ? '' : rule.thresholdLower,
        thresholdUpper: rule.thresholdUpper == null ? '' : rule.thresholdUpper,
        enabled: rule.enabled == null ? '0' : String(rule.enabled)
      }
    },
    metricValue(row, metric) {
      if (!row || !metric) return null
      if (metric.key === 'outletPressure') return row.outletPressure != null ? row.outletPressure : row.pressure
      if (metric.key === 'leakage') {
        if (row.leakage != null) return row.leakage
        if (row.inletLeak != null) return row.inletLeak
        return row.concentration
      }
      return row[metric.key]
    },
    formatValue(v) {
      if (v == null || v === '') return '—'
      return String(v)
    },
    categoryLabel(v) {
      return CATEGORY_MAP[v] || v || '—'
    },
    levelLabel(v) {
      return LEVEL_MAP[v] || '重要'
    },
    metricByKey(key) {
      return (this.metrics || []).find(m => m.key === key) || null
    },
    emptyCreateForm(section) {
      const d = this.device || {}
      const metric = section || (this.metrics || [])[0] || {}
      const metricKey = metric.key || ''
      const deviceType = d.deviceType || d.terminalType || ''
      const deviceCode = d.deviceCode || d.terminalCode || ''
      return {
        enabled: '0',
        alarmCategory: METRIC_ALARM_CATEGORY[metricKey] || 'flow',
        alarmLevel: 'warning',
        confirmMethod: '自动确认',
        compareOp: 'gt',
        deviceType,
        siteName: d.siteName || '',
        deviceCode,
        deviceId: d.deviceId || null,
        metricKey,
        ruleName: metric.label ? `${metric.label}报警` : '报警设置',
        thresholdUpper: '',
        thresholdLower: '',
        thresholdExtra: '',
        paramCondition: '',
        notifyChannels: ''
      }
    },
    openCreate(section) {
      this.createForm = this.emptyCreateForm(section)
      this.syncCreateParamCondition()
      this.createVisible = true
    },
    onCreateMetricChange(key) {
      const metric = this.metricByKey(key)
      this.createForm.alarmCategory = METRIC_ALARM_CATEGORY[key] || 'flow'
      this.createForm.ruleName = metric && metric.label ? `${metric.label}报警` : '报警设置'
      this.syncCreateParamCondition()
    },
    syncCreateParamCondition() {
      const metric = this.metricByKey(this.createForm.metricKey) || {}
      this.createForm.paramCondition = this.buildParamCondition(this.createForm, metric)
    },
    buildParamCondition(row, section) {
      const label = section.label || row.metricKey || ''
      const unit = section.unit || ''
      const lower = row.thresholdLower
      const upper = row.thresholdUpper
      if (lower !== '' && lower != null && upper !== '' && upper != null) {
        return `${label} < ${lower}${unit} 或 > ${upper}${unit}`
      }
      if (upper !== '' && upper != null) return `${label} > ${upper}${unit}`
      if (lower !== '' && lower != null) return `${label} < ${lower}${unit}`
      return row.paramCondition || ''
    },
    async createRule() {
      if (!this.createForm.ruleName) return this.$message.warning('请填写报警名称')
      if (!this.createForm.metricKey) return this.$message.warning('请选择监测指标')
      if (!this.createForm.deviceId && !this.createForm.deviceCode) {
        return this.$message.warning('当前记录未绑定设备，无法新增设备级报警设置')
      }
      if ((this.createForm.thresholdLower === '' || this.createForm.thresholdLower == null)
        && (this.createForm.thresholdUpper === '' || this.createForm.thresholdUpper == null)) {
        return this.$message.warning('请至少填写阈值上限或下限')
      }
      this.syncCreateParamCondition()
      this.createSaving = true
      try {
        await gasApi.alarmRule.save(this.createForm)
        this.$message.success('新增报警设置成功')
        this.createVisible = false
        this.$emit('saved')
      } finally {
        this.createSaving = false
      }
    },
    async saveRule(row, section) {
      if (!row.ruleId) return
      if (!row.ruleName) return this.$message.warning('请填写报警名称')
      if ((row.thresholdLower === '' || row.thresholdLower == null) && (row.thresholdUpper === '' || row.thresholdUpper == null)) {
        return this.$message.warning('请至少填写阈值上限或下限')
      }
      this.savingRuleId = row.ruleId
      try {
        const payload = {
          ...row,
          metricKey: row.metricKey || section.key,
          paramCondition: this.buildParamCondition(row, section)
        }
        await gasApi.alarmRule.save(payload)
        this.$message.success('报警设置已保存')
        this.$emit('saved')
      } finally {
        this.savingRuleId = null
      }
    },
    resetCreateForm() {
      this.createForm = {}
      this.createSaving = false
    },
    onClosed() {
      this.sections = []
      this.savingRuleId = null
      this.createVisible = false
      this.resetCreateForm()
    }
  }
}
</script>

<style scoped>
.alarm-drawer-wrap {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 18px 20px 24px;
  box-sizing: border-box;
  overflow: auto;
}
.drawer-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}
.title {
  font-size: 18px;
  font-weight: 600;
  color: #1d2129;
}
.sub {
  margin-top: 4px;
  font-size: 13px;
  color: #86909c;
}
.head-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.close-btn {
  font-size: 18px;
}
.hint {
  margin-bottom: 12px;
}
.metric-sections {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.metric-section {
  border: 1px solid #e5e6eb;
  border-radius: 6px;
  background: #fff;
  padding: 12px;
}
.metric-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  gap: 12px;
}
.metric-name {
  font-size: 15px;
  font-weight: 600;
  color: #1d2129;
}
.metric-unit {
  margin-left: 6px;
  color: #86909c;
  font-size: 12px;
}
.metric-current {
  font-size: 13px;
  color: #4e5969;
}
.metric-actions {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}
.rule-name {
  font-weight: 500;
  color: #1d2129;
}
.rule-desc {
  margin-top: 2px;
  font-size: 12px;
  color: #86909c;
}
.empty-rule {
  border: 1px dashed #c9cdd4;
  border-radius: 4px;
  padding: 12px;
  color: #86909c;
  background: #fafbfc;
}
</style>

<style>
.device-alarm-rules-drawer .el-drawer__body {
  height: 100%;
  overflow: hidden;
}
</style>
